import { useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '../api/errors';
import { useAppBar } from '../app/AppBar';
import { SESSION_QUERY_KEY } from '../auth/SessionProvider';
import { saveNotesScreenState } from '../history/notesScreenStore';
import type { NoteResponse } from '../notes/schema';
import { Icon } from '../ui/Icon';
import { uploadNote } from './api';
import { type CaptureAttempt, saveCaptureAttempt, takeCaptureAttempt } from './captureAttemptStore';
import styles from './capture.module.css';
import { generateIdempotencyKey } from './idempotencyKey';

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'preview'; readonly attempt: CaptureAttempt }
  | { readonly kind: 'uploading'; readonly attempt: CaptureAttempt; readonly progress: number }
  | { readonly kind: 'success'; readonly note: NoteResponse }
  | { readonly kind: 'read_failure'; readonly attempt: CaptureAttempt; readonly message: string };

interface Banner {
  readonly tone: 'error' | 'warning';
  readonly message: string;
}

const CONTINUOUS_COPY = {
  heading: 'Captura notas',
  description: 'Toma todas las fotos que necesites y súbelas cuando termines.',
  takePhoto: 'Tomar la siguiente foto',
  cameraStarting: 'Abriendo la cámara…',
  cameraUnavailable:
    'No se pudo abrir la vista de cámara. Puedes tomar las fotos con la cámara del teléfono.',
  insecureCamera:
    'La vista de cámara necesita una conexión segura (https). Puedes tomar las fotos con la cámara del teléfono.',
  cameraPermission:
    'No se permitió usar la cámara. Actívala en los permisos del navegador o usa la cámara del teléfono.',
  cameraMissing:
    'No se encontró una cámara disponible. Puedes tomar las fotos con la cámara del teléfono.',
  cameraBusy: 'Otra aplicación está usando la cámara. Ciérrala o usa la cámara del teléfono.',
  cameraFallback: 'Usar cámara del teléfono',
  startUpload: 'Subir notas',
  newBatch: 'Capturar más notas',
  waiting: 'foto esperando para subir',
  waitingPlural: 'fotos esperando para subir',
  uploading: 'Subiendo fotos en orden',
  complete: 'fotos enviadas',
  retry: 'Reintentar esta foto',
  discard: 'Descartar esta foto',
  remove: 'Quitar',
} as const;

type LiveCameraState = 'starting' | 'ready' | 'fallback';

/** Statuses answered `500`/`503` (plus the client's own network failure, status `0`): the
 * request never durably reached the server, so the same photo and the same idempotency key
 * are safe - and correct - to resend. */
const RETRY_SAME_KEY_STATUSES = new Set([0, 500, 503]);

/** A synchronous "this photo cannot be read" rejection from the upload itself (as opposed to a
 * later async recognition failure, which shows up on Notas instead) - shown as the redesign's
 * dedicated read-failure result screen rather than the generic inline banner every other 4xx
 * uses. See this task's reported schema gap: the exact code(s) `POST /notes` uses for this are
 * not yet confirmed against the backend, so this list is a best-effort mapping to revisit. */
const READ_FAILURE_CODES = new Set(['image_unreadable']);

function classifyFailure(
  error: ApiError,
): 'session_expired' | 'read_failure' | 'retry_same_key' | 'retry_new_key' {
  if (error.isSessionInvalid) {
    return 'session_expired';
  }
  if (READ_FAILURE_CODES.has(error.code)) {
    return 'read_failure';
  }
  if (RETRY_SAME_KEY_STATUSES.has(error.status)) {
    return 'retry_same_key';
  }
  return 'retry_new_key';
}

/** Duplicates are a warning shown beside a successful upload, never a reason to block it. */
function duplicateWarning(
  note: NoteResponse,
): { readonly message: string; readonly noteId: string } | null {
  if (note.duplicates.length === 0) {
    return null;
  }
  const first = note.duplicates[0];
  if (first === undefined) {
    return null;
  }
  const exact = note.duplicates.some((duplicate) => duplicate.kind === 'exact');
  return {
    message: exact
      ? 'Ya subiste una foto igual a esta. Se guardó de todas formas.'
      : 'Ya subiste una foto parecida a esta. Se guardó de todas formas.',
    noteId: first.note_id,
  };
}

function revoke(attempt: CaptureAttempt): void {
  URL.revokeObjectURL(attempt.previewUrl);
}

function buildAttempt(file: File, source: 'camera' | 'gallery'): CaptureAttempt {
  return {
    file,
    previewUrl: URL.createObjectURL(file),
    idempotencyKey: generateIdempotencyKey(),
    // A gallery pick may be an old photo; only a fresh camera shot is "now".
    capturedAt: source === 'camera' ? new Date().toISOString() : undefined,
  };
}

/**
 * Capture, the signed-in landing screen. Its live rear-camera view remains open while the owner
 * queues one or more separate notes, then submits that queue in one explicit action.
 */
export function CaptureScreen() {
  const queryClient = useQueryClient();
  const submittingRef = useRef(false);
  const continuousStartingRef = useRef(false);
  const continuousVideoRef = useRef<HTMLVideoElement>(null);
  const continuousStreamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // React development Strict Mode runs setup -> cleanup -> setup once to expose unsafe
    // effects. Restore the flag in setup so that check cannot make a mounted screen discard
    // upload progress and its durable response forever.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [phase, setPhase] = useState<Phase>(() => {
    const restored = takeCaptureAttempt();
    return restored ? { kind: 'preview', attempt: restored } : { kind: 'idle' };
  });
  const [banner, setBanner] = useState<Banner | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);
  const [continuousQueue, setContinuousQueue] = useState<CaptureAttempt[]>([]);
  const [continuousActive, setContinuousActive] = useState<CaptureAttempt | null>(null);
  const [continuousFailure, setContinuousFailure] = useState<{
    readonly attempt: CaptureAttempt;
    readonly message: string;
  } | null>(null);
  const [continuousComplete, setContinuousComplete] = useState(0);
  const [continuousBatchStarted, setContinuousBatchStarted] = useState(false);
  const [liveCameraState, setLiveCameraState] = useState<LiveCameraState>('starting');
  const [liveCameraMessage, setLiveCameraMessage] = useState<string | null>(null);

  useAppBar(() => ({ title: 'Captura' }), []);

  useEffect(() => {
    function stopStream(): void {
      continuousStreamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
      continuousStreamRef.current = null;
      if (continuousVideoRef.current !== null) {
        continuousVideoRef.current.srcObject = null;
      }
    }

    if (phase.kind !== 'idle' || continuousBatchStarted) {
      stopStream();
      return;
    }

    let cancelled = false;
    const showFallback = (message: string): void => {
      queueMicrotask(() => {
        if (!cancelled) {
          setLiveCameraState('fallback');
          setLiveCameraMessage(message);
        }
      });
    };

    if (!window.isSecureContext) {
      showFallback(CONTINUOUS_COPY.insecureCamera);
      return;
    }
    const mediaDevices = Reflect.get(navigator, 'mediaDevices') as MediaDevices | undefined;
    if (mediaDevices === undefined || typeof mediaDevices.getUserMedia !== 'function') {
      showFallback(CONTINUOUS_COPY.cameraUnavailable);
      return;
    }

    queueMicrotask(() => {
      if (!cancelled) {
        setLiveCameraState('starting');
        setLiveCameraMessage(null);
      }
    });
    void mediaDevices
      .getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' } } })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }
        continuousStreamRef.current = stream;
        const video = continuousVideoRef.current;
        if (video !== null) {
          video.srcObject = stream;
          void video.play().catch(() => undefined);
        }
        setLiveCameraState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        const name = error instanceof DOMException ? error.name : '';
        const message =
          name === 'NotAllowedError' || name === 'SecurityError'
            ? CONTINUOUS_COPY.cameraPermission
            : name === 'NotFoundError'
              ? CONTINUOUS_COPY.cameraMissing
              : name === 'NotReadableError'
                ? CONTINUOUS_COPY.cameraBusy
                : CONTINUOUS_COPY.cameraUnavailable;
        showFallback(message);
      });

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [continuousBatchStarted, phase.kind]);

  useEffect(() => {
    if (
      !continuousBatchStarted ||
      continuousActive !== null ||
      continuousFailure !== null ||
      continuousQueue.length === 0 ||
      continuousStartingRef.current
    ) {
      return;
    }

    const [attempt] = continuousQueue;
    if (attempt === undefined) {
      return;
    }
    continuousStartingRef.current = true;
    queueMicrotask(() => {
      setContinuousQueue((queue) => queue.slice(1));
      setContinuousActive(attempt);

      void uploadNote({
        file: attempt.file,
        idempotencyKey: attempt.idempotencyKey,
        capturedAt: attempt.capturedAt,
      })
        .then(() => {
          revoke(attempt);
          if (!mountedRef.current) {
            return;
          }
          continuousStartingRef.current = false;
          setContinuousActive(null);
          setContinuousComplete((count) => count + 1);
        })
        .catch((caught: unknown) => {
          if (!mountedRef.current) {
            return;
          }
          continuousStartingRef.current = false;
          const message = caught instanceof ApiError ? caught.message : 'No se pudo subir la foto.';
          setContinuousActive(null);
          setContinuousFailure({ attempt, message });
        });
    });
  }, [continuousActive, continuousBatchStarted, continuousFailure, continuousQueue]);

  function onContinuousFileChange(
    event: ChangeEvent<HTMLInputElement>,
    source: 'camera' | 'gallery',
  ): void {
    const file = event.target.files?.[0];
    if (file === undefined) {
      return;
    }
    setContinuousQueue((queue) => [...queue, buildAttempt(file, source)]);
  }

  function takeLiveCameraPhoto(): void {
    const video = continuousVideoRef.current;
    if (video === null || video.videoWidth === 0 || video.videoHeight === 0) {
      setLiveCameraMessage('La cámara todavía se está preparando. Intenta de nuevo en un momento.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context === null) {
      setLiveCameraMessage(CONTINUOUS_COPY.cameraUnavailable);
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob === null || !mountedRef.current || continuousBatchStarted) {
          return;
        }
        const file = new File([blob], `nota-${new Date().toISOString()}.jpg`, {
          type: 'image/jpeg',
        });
        setContinuousQueue((queue) => [...queue, buildAttempt(file, 'camera')]);
        setLiveCameraMessage(null);
      },
      'image/jpeg',
      0.95,
    );
  }

  function removeQueuedAttempt(idempotencyKey: string): void {
    setContinuousQueue((queue) => {
      const attempt = queue.find((candidate) => candidate.idempotencyKey === idempotencyKey);
      if (attempt !== undefined) {
        revoke(attempt);
      }
      return queue.filter((candidate) => candidate.idempotencyKey !== idempotencyKey);
    });
  }

  function startNewBatch(): void {
    if (continuousActive !== null || continuousQueue.length > 0 || continuousFailure !== null) {
      return;
    }
    setContinuousComplete(0);
    setContinuousBatchStarted(false);
    setLiveCameraState('starting');
    setLiveCameraMessage(null);
  }

  async function handleSubmit(): Promise<void> {
    if (phase.kind !== 'preview' || submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    const { attempt } = phase;
    setPhase({ kind: 'uploading', attempt, progress: 0 });
    setBanner(null);

    try {
      const note = await uploadNote({
        file: attempt.file,
        idempotencyKey: attempt.idempotencyKey,
        capturedAt: attempt.capturedAt,
        onProgress: (fraction) => {
          if (!mountedRef.current) {
            return;
          }
          setPhase((previous) =>
            previous.kind === 'uploading' ? { ...previous, progress: fraction } : previous,
          );
        },
      });
      submittingRef.current = false;
      if (!mountedRef.current) {
        return;
      }
      revoke(attempt);
      // Replace the progress view only after the durable API response has been validated.
      if (note.status === 'submitted' || note.status === 'processing') {
        // The list normally defaults to work that needs an owner decision. This link belongs to
        // one just-uploaded note, whose server state may advance before the owner taps it, so
        // use the unfiltered view rather than hiding either a still-processing or newly-ready
        // durable note behind a stale segment.
        saveNotesScreenState({ filter: 'all', scrollY: 0 });
      }
      setPhase({ kind: 'success', note });
    } catch (caught) {
      submittingRef.current = false;
      if (!(caught instanceof ApiError)) {
        throw caught;
      }

      const classification = classifyFailure(caught);

      if (classification === 'session_expired') {
        // Reuses the 6.1 convention: a session query refetch is what SessionProvider and
        // ProtectedRoute already react to. Save the attempt first so a remount at
        // `/capturar` after re-signing in (LoginScreen returns the owner to `location.state
        // .from`) can restore the same photo and the same idempotency key.
        saveCaptureAttempt(attempt);
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
        if (!mountedRef.current) {
          return;
        }
        // The redirect did not happen - the session was valid after all. Recover locally
        // instead of leaving the saved attempt to be silently picked up by some later mount.
        takeCaptureAttempt();
        setPhase({ kind: 'preview', attempt });
        setBanner({ tone: 'error', message: caught.message });
        return;
      }

      if (classification === 'read_failure') {
        setPhase({ kind: 'read_failure', attempt, message: caught.message });
        return;
      }

      if (classification === 'retry_same_key') {
        setPhase({ kind: 'preview', attempt });
        setBanner({ tone: 'error', message: caught.message });
        return;
      }

      revoke(attempt);
      setPhase({ kind: 'idle' });
      setBanner({ tone: 'error', message: caught.message });
    }
  }

  function handleCaptureAnother(): void {
    setPhase({ kind: 'idle' });
    setBanner(null);
    setDuplicateDismissed(false);
  }

  function handleRetakeFromFailure(): void {
    if (phase.kind === 'read_failure') {
      revoke(phase.attempt);
    }
    setPhase({ kind: 'idle' });
    setBanner(null);
  }

  const duplicate = phase.kind === 'success' ? duplicateWarning(phase.note) : null;

  return (
    <div className={styles.screen}>
      {banner ? (
        <p
          className={banner.tone === 'error' ? styles.errorBanner : styles.warningBanner}
          role="alert"
        >
          {banner.message}
        </p>
      ) : null}

      {phase.kind === 'idle' ? (
        <section className={styles.continuousCard} aria-label={CONTINUOUS_COPY.heading}>
          <h2 className={styles.confirmHeading}>{CONTINUOUS_COPY.heading}</h2>
          <p className={styles.continuousDescription}>{CONTINUOUS_COPY.description}</p>
          {!continuousBatchStarted && liveCameraState !== 'fallback' ? (
            <div className={styles.liveCameraWrap}>
              <video
                ref={continuousVideoRef}
                className={styles.liveCamera}
                aria-label="Vista de cámara"
                autoPlay
                muted
                playsInline
              />
              {liveCameraState === 'starting' ? (
                <p className={styles.liveCameraLoading} role="status">
                  {CONTINUOUS_COPY.cameraStarting}
                </p>
              ) : null}
              <button
                type="button"
                className={styles.continuousCameraButton}
                onClick={takeLiveCameraPhoto}
                disabled={liveCameraState !== 'ready'}
              >
                {CONTINUOUS_COPY.takePhoto}
              </button>
            </div>
          ) : null}
          {!continuousBatchStarted && liveCameraState === 'fallback' ? (
            <>
              {liveCameraMessage !== null ? (
                <p className={styles.errorBanner}>{liveCameraMessage}</p>
              ) : null}
              <label className={styles.continuousCameraButton}>
                {CONTINUOUS_COPY.cameraFallback}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  aria-label={CONTINUOUS_COPY.takePhoto}
                  className={styles.continuousCameraInput}
                  onChange={(event) => {
                    onContinuousFileChange(event, 'camera');
                  }}
                />
              </label>
            </>
          ) : null}
          {!continuousBatchStarted ? (
            <label className={styles.galleryRow}>
              <span aria-hidden="true" className={styles.galleryLinkText}>
                <Icon name="image" size={20} />
                Elegir de la galería
              </span>
              <input
                type="file"
                accept="image/*"
                aria-label="Elegir de la galería"
                className={styles.galleryInput}
                onChange={(event) => {
                  onContinuousFileChange(event, 'gallery');
                }}
              />
            </label>
          ) : null}
          {continuousQueue.length > 0 ? (
            <ol className={styles.continuousQueue} aria-label="Fotos en espera">
              {continuousQueue.map((attempt, index) => (
                <li key={attempt.idempotencyKey} className={styles.continuousQueueItem}>
                  <img src={attempt.previewUrl} alt={`Foto ${(index + 1).toString()} en espera`} />
                  <span className={styles.continuousQueueName}>
                    {(index + 1).toString()}. {attempt.file.name}
                  </span>
                  {!continuousBatchStarted ? (
                    <button
                      type="button"
                      className={styles.continuousRemoveButton}
                      onClick={() => {
                        removeQueuedAttempt(attempt.idempotencyKey);
                      }}
                    >
                      {CONTINUOUS_COPY.remove}
                    </button>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}
          <p className={styles.continuousStatus} role="status" aria-live="polite">
            {continuousActive !== null ? `${CONTINUOUS_COPY.uploading}. ` : ''}
            {continuousQueue.length > 0
              ? `${continuousQueue.length.toString()} ${
                  continuousQueue.length === 1
                    ? CONTINUOUS_COPY.waiting
                    : CONTINUOUS_COPY.waitingPlural
                }. `
              : ''}
            {continuousComplete > 0
              ? `${continuousComplete.toString()} ${CONTINUOUS_COPY.complete}.`
              : 'Aún no hay fotos en la fila.'}
          </p>
          {!continuousBatchStarted && continuousQueue.length > 0 ? (
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => {
                setContinuousBatchStarted(true);
              }}
            >
              {`${CONTINUOUS_COPY.startUpload} ${continuousQueue.length.toString()} ${
                continuousQueue.length === 1 ? 'nota' : 'notas'
              }`}
            </button>
          ) : null}
          {continuousFailure !== null ? (
            <div className={styles.errorBanner} role="alert">
              <p>{continuousFailure.message}</p>
              <div className={styles.continuousFailureActions}>
                <button
                  type="button"
                  className={styles.retryButton}
                  onClick={() => {
                    setContinuousQueue((queue) => [continuousFailure.attempt, ...queue]);
                    setContinuousFailure(null);
                  }}
                >
                  {CONTINUOUS_COPY.retry}
                </button>
                <button
                  type="button"
                  className={styles.retakeLink}
                  onClick={() => {
                    revoke(continuousFailure.attempt);
                    setContinuousFailure(null);
                  }}
                >
                  {CONTINUOUS_COPY.discard}
                </button>
              </div>
            </div>
          ) : null}
          {continuousBatchStarted &&
          continuousActive === null &&
          continuousQueue.length === 0 &&
          continuousFailure === null ? (
            <button type="button" className={styles.retakeLink} onClick={startNewBatch}>
              {CONTINUOUS_COPY.newBatch}
            </button>
          ) : null}
        </section>
      ) : null}

      {phase.kind === 'preview' || phase.kind === 'uploading' ? (
        <>
          <h2 className={styles.confirmHeading}>¿Se leen los renglones?</h2>
          <button
            type="button"
            className={styles.previewCard}
            onClick={() => {
              setZoomed(true);
            }}
          >
            <img
              src={phase.attempt.previewUrl}
              alt="Vista previa de la nota"
              className={styles.previewImage}
            />
          </button>

          {phase.kind === 'uploading' ? (
            <div className={styles.progressWrap} role="status" aria-live="polite">
              <p className={styles.progressText}>Subiendo… {Math.round(phase.progress * 100)}%</p>
              <progress className={styles.progressBar} value={phase.progress} max={1} />
            </div>
          ) : (
            <>
              <button
                type="button"
                className={styles.submitButton}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                Subir esta nota
              </button>
              <button
                type="button"
                className={styles.retakeLink}
                onClick={() => {
                  revoke(phase.attempt);
                  setPhase({ kind: 'idle' });
                  setBanner(null);
                }}
              >
                Volver a tomar
              </button>
            </>
          )}
        </>
      ) : null}

      {phase.kind === 'success' ? (
        <div className={styles.successCard} role="status">
          <p className={styles.successHeading}>
            <span className={`${styles.resultDisc ?? ''} ${styles.resultDiscOk ?? ''}`}>
              <Icon name="check" size={18} strokeWidth={2.4} />
            </span>
            Nota enviada
          </p>
          <p className={styles.successText}>
            {phase.note.status === 'submitted'
              ? 'La nota se guardó y está en fila para leerse.'
              : phase.note.status === 'processing'
                ? 'La nota se está leyendo ahora.'
                : 'La nota se guardó. Consulta su estado en Notas.'}{' '}
            Puedes seguir capturando o ver su estado en Notas.
          </p>
          {duplicate !== null && !duplicateDismissed ? (
            <div className={styles.duplicateBlock} role="alert">
              <p>{duplicate.message}</p>
              <div className={styles.duplicateActions}>
                <Link className={styles.duplicateCompareLink} to={`/notas/${duplicate.noteId}`}>
                  Comparar
                </Link>
                <button
                  type="button"
                  className={styles.duplicateDismissButton}
                  onClick={() => {
                    setDuplicateDismissed(true);
                  }}
                >
                  Está bien así
                </button>
              </div>
            </div>
          ) : null}
          <div className={styles.successActions}>
            <button type="button" className={styles.submitButton} onClick={handleCaptureAnother}>
              Tomar otra nota
            </button>
            <Link className={styles.secondaryLink} to="/notas">
              Ver esta nota en Notas
            </Link>
          </div>
        </div>
      ) : null}

      {phase.kind === 'read_failure' ? (
        <div className={styles.failureCard} role="alert">
          <p className={styles.failureHeading}>
            <span className={`${styles.resultDisc ?? ''} ${styles.resultDiscDanger ?? ''}`}>
              <Icon name="x-circle" size={18} strokeWidth={2.4} />
            </span>
            No se pudo leer
          </p>
          <p className={styles.successText}>{phase.message}</p>
          <ul className={styles.failureTipList}>
            <li>
              <strong>Luz:</strong> evita sombras y reflejos sobre el papel.
            </li>
            <li>
              <strong>Pulso:</strong> mantén firme el teléfono para que no salga borrosa.
            </li>
          </ul>
          <button type="button" className={styles.submitButton} onClick={handleRetakeFromFailure}>
            Volver a tomar
          </button>
        </div>
      ) : null}

      {zoomed && (phase.kind === 'preview' || phase.kind === 'uploading') ? (
        <div
          className={styles.zoomOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Foto ampliada"
          onClick={() => {
            setZoomed(false);
          }}
        >
          <button
            type="button"
            className={styles.zoomClose}
            onClick={() => {
              setZoomed(false);
            }}
          >
            Cerrar
          </button>
          <img
            className={styles.zoomImage}
            src={phase.attempt.previewUrl}
            alt="Vista previa de la nota"
          />
        </div>
      ) : null}
    </div>
  );
}
